"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  label: "Όλα τα Άρθρα",
  value: "all"
}, {
  label: "Smart Travel",
  value: "budget-travel"
}, {
  label: "Hidden Gems",
  value: "hidden-gems"
}, {
  label: "Hotel Guides",
  value: "hotel-guides"
}, {
  label: "Τοπικές Εμπειρίες",
  value: "local-experiences"
}, {
  label: "Έξυπνες Κρατήσεις",
  value: "booking-tips"
}];
const fallbackPosts = [{
  id: "1",
  title: "Πώς να ξεκλειδώσετε τις χαμηλότερες τιμές στα ελληνικά νησιά",
  excerpt: "Ένας πλήρης οδηγός για το πώς να εξασφαλίσετε αποκλειστικές προσφορές και να κάνετε έξυπνες κρατήσεις χωρίς μεσάζοντες.",
  category: "Έξυπνες Κρατήσεις",
  readTime: "12 min read",
  author: "HotelDeals Editorial",
  date: "14 Μαρτίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/greek_island_budget_guide.webp",
  aspect: "aspect-[4/5]",
  featured: true
}, {
  id: "2",
  title: "Άνδρος: Ο απόλυτος οδηγός για smart αποδράσεις",
  excerpt: "Ανακαλύψτε τις καλύτερες προσφορές σε καταλύματα και φαγητό στο αρχοντικό νησί των Κυκλάδων.",
  category: "Smart Travel",
  readTime: "8 min read",
  author: "Γιώργος Παππάς",
  date: "09 Μαρτίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/andros_scenic_view.png",
  aspect: "aspect-[3/4]"
}, {
  id: "3",
  title: "Τα 5 καλύτερα boutique ξενοδοχεία στην Αθήνα κάτω από 100€",
  excerpt: "Πολυτέλεια και στυλ στην καρδιά της πρωτεύουσας χωρίς να ξοδέψετε μια περιουσία.",
  category: "Hotel Guides",
  readTime: "10 min read",
  author: "Ελένη Κώστα",
  date: "02 Μαρτίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/athens_boutique_hotels.png",
  aspect: "aspect-square"
}, {
  id: "4",
  title: "Η τέχνη της έξυπνης κράτησης: Γιατί κερδίζετε πάντα",
  excerpt: "Αναλύουμε τα οφέλη των αποκλειστικών deals και πώς να εξασφαλίσετε την καλύτερη δυνατή τιμή για τη διαμονή σας.",
  category: "Έξυπνες Κρατήσεις",
  readTime: "7 min read",
  author: "HotelDeals Team",
  date: "24 Φεβρουαρίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/direct_booking_benefits.png",
  aspect: "aspect-[5/4]"
}, {
  id: "5",
  title: "Case Study: Πώς μια οικογένεια γλίτωσε 400€ στις διακοπές της",
  excerpt: "Πραγματικά παραδείγματα εξοικονόμησης χρημάτων χρησιμοποιώντας τις έξυπνες λύσεις του HotelDeals.gr.",
  category: "Smart Travel",
  readTime: "15 min read",
  author: "Μαρία Δήμου",
  date: "18 Φεβρουαρίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/family_travel_savings.png",
  aspect: "aspect-[3/4]"
}, {
  id: "6",
  title: "Νέες τάσεις στον τουρισμό: Η άνοδος του Slow Travel",
  excerpt: "Γιατί οι ταξιδιώτες επιλέγουν πλέον λιγότερους προορισμούς και περισσότερη ουσία.",
  category: "Τοπικές Εμπειρίες",
  readTime: "9 min read",
  author: "Γιώργος Παππάς",
  date: "11 Φεβρουαρίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/slow_travel_greece.png",
  aspect: "aspect-[4/5]"
}, {
  id: "7",
  title: "Κρυμμένα χωριά της Ηπείρου για το επόμενο Σαββατοκύριακο",
  excerpt: "Προτάσεις για διαμονή σε παραδοσιακούς ξενώνες μακριά από τον μαζικό τουρισμό.",
  category: "Hidden Gems",
  readTime: "6 min read",
  author: "Ελένη Κώστα",
  date: "04 Φεβρουαρίου 2024",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698288/img/epirus_hidden_villages.png",
  aspect: "aspect-square"
}];
function JournalConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const displayPosts = !loading && !error && posts.length > 0 ? posts.map((p, i) => ({
    id: p.id,
    title: p.title,
    excerpt: (p.excerpt || "").replace(/<[^>]*>/g, "").slice(0, 160),
    category: p.categories?.[0]?.name || "Οδηγός",
    readTime: `${Math.max(5, Math.ceil((p.content?.length || 1000) / 1000))} min read`,
    author: p.author?.name || "Editorial",
    date: p.date ? new Date(p.date).toLocaleDateString("el-GR", {
      year: "numeric",
      month: "long",
      day: "2-digit"
    }) : "",
    image: p.featuredImage || fallbackPosts[i % fallbackPosts.length].image,
    link: p.link || "#",
    aspect: fallbackPosts[i % fallbackPosts.length].aspect,
    featured: i === 0
  })) : fallbackPosts.map(p => ({
    ...p,
    link: "#"
  }));
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-[4/5] bg-muted animate-pulse rounded-md" />
            <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
            <div className="h-6 bg-muted animate-pulse rounded w-5/6" />
          </div>)}
      </div>;
  }
  const featuredPost = displayPosts[0];
  const restPosts = displayPosts.slice(1);
  return <>
      <div className="columns-1 md:columns-2 lg:columns-3 gap-8 [column-fill:_balance]">
        {featuredPost && <article data-index={0} className="group mb-12 break-inside-avoid lg:column-span-2" style={{
        columnSpan: "all" as any
      }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <Link to={featuredPost.link} className="block overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden bg-muted rounded-md">
                  <img src={featuredPost.image} data-wvc-srcset={`${featuredPost.image}-wvc-srcset`} data-wvc-sizes={`${featuredPost.image}-wvc-sizes`} alt={featuredPost.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                </div>
              </Link>
              <div className="flex flex-col">
                <div className="text-[11px] tracking-[0.2em] uppercase font-mono mb-4 text-primary">
                  ΚΥΡΙΟ ΑΡΘΡΟ · {featuredPost.category} · {featuredPost.readTime}
                </div>
                <Link to={featuredPost.link} className="block">
                  <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.1] mb-5 transition-transform duration-300 group-hover:-translate-y-1 text-foreground">
                    {featuredPost.title}
                  </h3>
                </Link>
                <p className="text-base leading-relaxed text-muted-foreground mb-6 max-w-xl font-default">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="font-mono text-xs tracking-wider uppercase text-muted-foreground">
                    {featuredPost.date}
                  </div>
                  <Link to="https://hoteldeals.vlmtravel.com/#/welcome" newTab className="inline-flex items-center justify-center px-6 py-2 bg-primary text-white text-xs font-mono tracking-wider uppercase font-bold rounded-lg hover:bg-primary/90 transition-all duration-300">
                    Κάντε κράτηση μέσω του Hotel Deals
                  </Link>
                </div>
              </div>
            </div>
          </article>}

        {restPosts.map((post, i) => <article key={post.id} data-index={i + 1} className="group mb-10 break-inside-avoid">
            <Link to={post.link} className="block overflow-hidden mb-5">
              <div className={`${post.aspect} overflow-hidden bg-muted rounded-md`}>
                <img src={post.image} data-wvc-srcset={`${post.image}-wvc-srcset`} data-wvc-sizes={`${post.image}-wvc-sizes`} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
            </Link>
            <div className="text-[10px] tracking-[0.2em] uppercase font-mono mb-3 text-primary">
              {post.category} · {post.readTime}
            </div>
            <Link to={post.link} className="block">
              <h3 className={`font-default font-semibold leading-[1.2] mb-3 transition-transform duration-300 group-hover:-translate-y-1 text-foreground ${i % 3 === 0 ? "text-2xl md:text-[32px]" : i % 3 === 1 ? "text-xl md:text-[26px]" : "text-xl md:text-[28px]"}`}>
                {post.title}
              </h3>
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground mb-4 line-clamp-2 font-default">
              {post.excerpt}
            </p>
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="font-mono text-[11px] tracking-wider uppercase text-muted-foreground">
                {post.date}
              </div>
              <Link to="https://hoteldeals.vlmtravel.com/#/welcome" newTab className="inline-flex items-center justify-center px-6 py-2 bg-primary text-white text-[10px] font-mono tracking-wider uppercase font-bold rounded-lg hover:bg-primary/90 transition-all duration-300">
                Κάντε κράτηση μέσω του Hotel Deals
              </Link>
            </div>
          </article>)}
      </div>

      <div className="flex justify-center pt-16">
        <Link to="https://hoteldeals.gr/%cf%84%ce%b1%ce%be%ce%b9%ce%b4%ce%b9%cf%89%cf%84%ce%b9%ce%ba%ce%bf%ce%af-%ce%bf%ce%b4%ce%b7%ce%b3%ce%bf%ce%af/" className="group font-mono text-xs tracking-[0.25em] bg-transparent hover:bg-transparent h-auto px-2 py-2 rounded-md text-primary inline-block">
          <span className="relative inline-block">
            ΠΕΡΙΣΣΟΤΕΡΑ ΑΡΘΡΑ
            <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-100 transition-transform duration-500 group-hover:scale-x-110 bg-primary" />
          </span>
        </Link>
      </div>

      {error && <p className="text-center text-xs text-muted-foreground mt-6 font-mono tracking-wider">
          Αδυναμία φόρτωσης άρθρων. Εμφάνιση αρχείου.
        </p>}
    </>;
}
export default function TheIpJournal() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4365" id="οδηγοί-για-οικονομικές-διακοπές" className="bg-background text-foreground pt-40 pb-24 px-6 md:px-10 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <div className="h-px w-full bg-border mb-10" />

        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 md:gap-4 mb-16">
          <div className="font-mono text-xs tracking-[0.25em] order-2 md:order-1 text-left text-primary">
            ΤΟΜΟΣ 2024 / ΤΕΥΧΟΣ 01
          </div>
          <div className="order-1 md:order-2 text-center">
            <h1 className="font-default font-bold text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-foreground">
              Exclusive <span className="italic font-serif font-normal">Deals</span>.
            </h1>
          </div>
          <div className="order-3 text-left md:text-right">
            <Link to="#" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-primary">
              <span className="relative inline-block">
                ΕΓΓΡΑΦΗ
                <span className="absolute -bottom-1 left-0 h-px w-full transition-transform duration-500 group-hover:translate-x-1 bg-primary" />
              </span>
              <ArrowRight className="size-3 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="border-y border-border mb-16">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-8 md:gap-x-10 gap-y-3 py-5 overflow-x-auto">
            {categories.map((cat, i) => <Button key={cat.value} data-index={i} variant="ghost" className={`group relative font-mono text-[11px] tracking-[0.22em] whitespace-nowrap h-auto p-0 transition-colors rounded-md ${i === 0 ? "text-foreground" : "text-muted-foreground hover:text-foreground"} hover:bg-transparent`}>
                {cat.label}
                <span className={`absolute -bottom-1.5 left-0 h-px w-full transition-transform duration-300 origin-left bg-primary ${i === 0 ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
              </Button>)}
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <JournalConsumer />
        </PostsProvider>

        <div className="flex justify-center mt-12">
          <Link to="https://hoteldeals.gr/%cf%84%ce%b1%ce%be%ce%b9%ce%b4%ce%b9%cf%89%cf%84%ce%b9%ce%ba%ce%bf%ce%af-%ce%bf%ce%b4%ce%b7%ce%b3%ce%bf%ce%af/">
            <Button className="bg-primary text-primary-foreground px-10 py-6 text-lg font-bold rounded-lg hover:scale-105 transition-transform duration-300">
              Δείτε όλους τους Ταξιδιωτικούς Οδηγούς
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}